import { FunctionComponent, useCallback, useState } from 'react'
import { Avatar, Menu, ConexaLogo } from '@conexasaude/conexa-hero'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { useLocation, useHistory } from 'react-router-dom'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import { RenderIf } from '../RenderIf'
import { useNavbar } from './useNavbar'
import useStyles from '@/components/Navbar/styles'
import logo from '@/assets/logo-conexa.svg'
import useBreakpoints from '@/utils/useBreakpoints'
import { NavbarProps } from './types'
import { NOT_ALLOWED_STATUS } from './constants'
import { useAttendanceCallStore } from '@/modules/RealtimeAttendance/store/useAttendanceCall'
import { DependentsModal } from '@/modules/Activation/components/DependentsModal'
import GetDependentData from '@/modules/Activation/mappers/GetDependentData'
import { useModalContext } from '../Modal/contexts/ModalContext'
import { showErrorToast } from '@/modules/Activation/utils/requisitionErrors'
import { useNavigation } from '@/hooks/useNavigation'
import themeServices from '@/theme/theme.services'
import service from '@/modules/Auth/auth-services'
import { User } from '@/modules/Auth/classes/User'
import { useMedicalSpecialitiesContext } from '@/modules/MedicalSpecialities/contexts/MedicalSpecialitiesContext'
import AvatarWithDependentsAccessWrapper from '../AvatarWithDependentsAccessWrapper'

const Navbar: FunctionComponent<NavbarProps> = ({
  hideMenuOptions = false,
  showAvatar = true,
}) => {
  const { embed, setThemeColors } = useThemeGeneratorContext()
  const { user, users, clinic, choiceUser, setUserClinic } = useAuthContext()
  const { pathname } = useLocation()
  const { logAnalyticsEvent } = useFirebaseContext()
  const { isPhone } = useBreakpoints()
  const { states } = useNavbar()
  const history = useHistory()
  const { showModal, closeModal } = useModalContext()
  const { setSpecialities } = useMedicalSpecialitiesContext()
  const [opened, setOpened] = useState(false)
  const navigate = useNavigation()
  const styles = useStyles({
    embed: embed?.value,
    conexaLogo: !clinic?.logos?.show,
  })

  const attendanceCall = useAttendanceCallStore(store => store.attendanceCall)
  const showTagAvatar = () => {
    if (user?.holder === 'TITULAR') {
      return (
        user?.childrenDependents?.length > 0 ||
        user?.adultDependents?.length > 0
      )
    }
    return user?.holder === 'DEPENDENTE'
  }

  const showInternalAvatarButton = () => {
    if (user?.holder === 'TITULAR') {
      return (
        user?.childrenDependents?.length > 0 ||
        user?.adultDependents?.length > 0
      )
    }
    return user?.holder === 'DEPENDENTE' && user?.isMinorPatient
  }

  const handleAnalytics = useCallback(
    (eventName: string) => {
      if (!eventName) return
      logAnalyticsEvent(eventName)
    },
    [logAnalyticsEvent],
  )

  const handleNavigation = useCallback(
    (
      path: string,
      analyticsEventName: string,
      itemType: string,
      callback?: (...args: unknown[]) => void,
    ) => {
      const hasButtonType = (itemType: string) => /button/.test(itemType)
      handleAnalytics(analyticsEventName)

      if (hasButtonType(itemType) && callback) {
        return callback()
      }

      history.push(path)
    },
    [history, handleAnalytics],
  )

  const isActiveNavItem = useCallback(
    (itemPath: string) => {
      const homePath = '/'

      if (pathname === homePath && itemPath === pathname) return true
      else if (itemPath !== homePath) return pathname.includes(itemPath)
      return false
    },
    [pathname],
  )

  const handleProfileNavigation = useCallback(
    (item: (typeof states.profileAvatarItems)[0]) => {
      handleAnalytics(item.analyticsEvent)
      item.onClick()
    },
    [handleAnalytics, states],
  )

  const profileActions = states.profileAvatarItems
    ?.filter(item => item.show)
    .map(item => ({
      id: item.id,
      label: item.label,
      onClick: !NOT_ALLOWED_STATUS.includes(attendanceCall?.queueStatus)
        ? () => handleProfileNavigation(item)
        : null,
    }))

  const onAvatarCustomHeaderClick = () => {
    if (user?.holder === 'TITULAR') {
      showModal('DEPENDENTS_MODAL_NAVBAR', {
        title: 'Meus dependentes',
        width: 480,
      })
    } else {
      const holder = users?.find(
        holder => holder?.patientId === user?.responsable?.id,
      )
      setUser(holder)
    }
  }

  const setUser = useCallback(
    async (user: User) => {
      try {
        const { token, id, flagAccessedBefore } = user

        const [userClinic, clinicColors, ip] = await Promise.all([
          service.getClinic(token),
          themeServices.getClinicColorsById(user?.clinic?.id),
          service.getUserIP().catch(() => {
            return ''
          }),
        ])

        const { clinic, flags } = userClinic

        if (!flagAccessedBefore) await service.validateFirstAccess(token)

        choiceUser(id, ip)

        setThemeColors(clinicColors)
        setUserClinic(userClinic)
        setSpecialities(clinic)

        if (flags?.PermiteGeolocalizacao) {
          navigator.geolocation.getCurrentPosition(position => {
            const {
              accuracy,
              altitude,
              altitudeAccuracy,
              heading,
              latitude,
              longitude,
              speed,
            } = position.coords
            service.sendGeolocation({
              accuracy,
              altitude,
              altitudeAccuracy,
              heading,
              latitude,
              longitude,
              speed,
            })
          })
        }

        navigate('/')
      } catch (err) {
        showErrorToast(err)
      } finally {
        closeModal()
      }
    },
    [
      choiceUser,
      setThemeColors,
      setUserClinic,
      setSpecialities,
      navigate,
      closeModal,
    ],
  )

  const toggle = () => {
    setOpened(prevState => !prevState)
  }

  const uniqueById = dependents => {
    const seenIds = new Set()
    return dependents?.filter(dependent => {
      if (!seenIds.has(dependent.idUsuario)) {
        seenIds.add(dependent.idUsuario)
        return true
      }
      return false
    })
  }

  const adultDependents = uniqueById(user?.adultDependents)
  const childrenDependents = uniqueById(user?.childrenDependents)

  return (
    <div>
      <RenderIf condition={!embed.value || embed.navbarEmbed}>
        <Menu.Root maxWidth="xl">
          <Menu.Brand>
            <Menu.Toggle />
            <RenderIf condition={!clinic?.logos.show}>
              <ConexaLogo base="colorful" inverse />
            </RenderIf>
            <RenderIf condition={clinic?.logos.show}>
              <img
                css={styles.navLogo}
                src={clinic?.logos.show ? clinic.logos.menu : logo}
                alt="Logo"
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null
                  currentTarget.src = logo
                }}
              />
            </RenderIf>
          </Menu.Brand>
          <RenderIf condition={!hideMenuOptions}>
            <Menu.List>
              {states.menuItems?.map(item => (
                <RenderIf condition={item?.show} key={item.id}>
                  <Menu.Trigger
                    leftIcon={item?.icon}
                    onClick={() =>
                      handleNavigation(
                        item?.path,
                        item?.analyticsEvent,
                        item?.type,
                        item?.onClick,
                      )
                    }
                    isActive={isActiveNavItem(item.path)}
                  >
                    {item?.label}
                  </Menu.Trigger>
                </RenderIf>
              ))}
            </Menu.List>
          </RenderIf>
          <Menu.Tools>
            <RenderIf condition={showAvatar}>
              <Avatar
                src={user?.photo}
                label={isPhone ? null : user?.shortName}
                customHeaderItem={closeAction => (
                  <AvatarWithDependentsAccessWrapper
                    user={user}
                    showTagAvatar={showTagAvatar}
                    showButtonAvatar={showInternalAvatarButton}
                    onAvatarCustomHeaderClick={onAvatarCustomHeaderClick}
                    toggle={toggle}
                    closeAction={closeAction}
                  />
                )}
                menu
                maxLabelLength={12}
                firstName
                size="medium"
                actionListItem={profileActions}
                width={showTagAvatar() ? 310 : 290}
              />
            </RenderIf>
          </Menu.Tools>
        </Menu.Root>
        <RenderIf
          condition={
            opened &&
            (user?.adultDependents?.length > 0 ||
              user?.childrenDependents?.length > 0)
          }
        >
          <DependentsModal
            modalId="DEPENDENTS_MODAL_NAVBAR"
            adultDependents={GetDependentData.toDomain(adultDependents)}
            childDependents={GetDependentData.toDomain(childrenDependents)}
          />
        </RenderIf>
      </RenderIf>
    </div>
  )
}

export default Navbar
